/* eslint-disable no-useless-constructor */
/* eslint-disable no-empty-function */
import Echo from '@ably/laravel-echo';

import { Nullable } from '../../../../types';
import { WebsocketServiceConfig } from './types/WebsocketServiceConfig';
import { WebsocketServiceChannel } from './WebsocketServiceChannel';

export class WebsocketService<Channel extends object> {
  private channels: Record<string, WebsocketServiceChannel<Channel>> = {};

  private echo: Nullable<Echo> = null;

  constructor(private readonly config: WebsocketServiceConfig<Channel>) {}

  /**
   * Supply (or clear) the active connection. Reconciles all known channels:
   * detaches from the previous connection and attaches to the new one. This
   * carries over subscriptions made before a connection existed, re-subscribes
   * everything when the identity changes (a new Echo instance), and tears down
   * cleanly on logout (`echo === null`).
   */
  public setConnection(echo: Nullable<Echo>): void {
    Object.values(this.channels).forEach((channel) => channel.detach());
    this.echo = echo;
    if (echo) {
      Object.values(this.channels).forEach((channel) => channel.attach(echo));
    }
  }

  private initializeChannel(channelName: string): void {
    const channel = new WebsocketServiceChannel<Channel>(
      channelName,
      this.config.channelApi,
      this.onChannelCleanUp.bind(this),
    );
    this.channels[channelName] = channel;
    if (this.echo) {
      channel.attach(this.echo);
    }
  }

  private onChannelCleanUp(channelName: string): void {
    if (this.channels[channelName]) {
      this.channels[channelName].destroy();
      delete this.channels[channelName];
    }
  }

  public subscribeToEvent<T>(
    channelName: string,
    eventName: string,
    callback: (data: T) => void,
  ): () => void {
    if (!this.channels[channelName]) {
      this.initializeChannel(channelName);
    }
    return this.channels[channelName].addEventCallback(eventName, callback);
  }

  public destroy(): void {
    Object.keys(this.channels).forEach((channelName) =>
      this.onChannelCleanUp(channelName),
    );
    this.echo = null;
  }
}
